/* eslint-disable */
import { Auth0Client, createAuth0Client } from "@auth0/auth0-spa-js"
import liff from "@line/liff"
import { set } from "date-fns"
import React, { createContext, useContext, useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { USER_TYPE } from "src/modules/const"
import { Company, CompanyService, Tag, TagService, UserService } from "src/openapi"
import { setDatadogUser, clearDatadogUser } from "src/lib/datadog"

type IUser = {
  username: string
  email: string
  type: string
  profileURL: string
  token: string
  authID: string
  id: number
  companyID: number // 自分の所属する企業ID
}

interface IUserAuthContextProvider {
  children: React.ReactNode
}

interface IUserAuthContext {
  session: { accessToken: string } | undefined
  setSession: (value: { accessToken: string } | undefined) => void
  auth0Client: Auth0Client | undefined
  user: IUser | null
  lineIdToken: string | null
  logout: any
}

const UserAuthContext = createContext<IUserAuthContext | null>(null)

export const LoginProvider: React.FC<IUserAuthContextProvider> = ({ children }) => {
  const navigate = useNavigate()
  const [session, setSession] = useState<{ accessToken: string } | undefined>(undefined)
  const [auth0Client, setAuth0Client] = useState<Auth0Client | undefined>(undefined)
  const [token, setToken] = useState<string>("")
  const [user, setUser] = useState<IUser | null>(null)
  const [lineIdToken, setLineIdToken] = useState<string | null>(null)

  useEffect(() => {
    // Set the Auth0 API Client.
    const fetchClient = async () => {
      const tmpAuth0Client = await createAuth0Client({
        domain: process.env.REACT_APP_AUTH0_DOMAIN as string,
        clientId: process.env.REACT_APP_AUTH0_CLIENT_ID as string,
        cacheLocation: "localstorage",
        authorizationParams: {
          redirect_uri: process.env.REACT_APP_AUTH0_CALLBACK,
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      })
      setAuth0Client(tmpAuth0Client)
    }
    fetchClient().catch((err) => console.error(err))
  }, [])

  const isInAuthProcess = () => {
    const path = window.location.pathname
    return path === "/callback"
  }

  const performLoginRedirect = async () => {
    if (!auth0Client || isInAuthProcess()) {
      return
    }

    try {
      await auth0Client.loginWithRedirect({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      })
    } catch (err) {
      console.error("Login redirect error:", err)
    }
  }

  useEffect(() => {
    if (auth0Client && !liff.isInClient()) {
      const checkIsAuthenticated = async () => {
        try {
          // Handle Auth0 callback if present
          if (isInAuthProcess()) {
            try {
              await auth0Client.handleRedirectCallback()
              window.history.replaceState({}, document.title, window.location.pathname)

              const token = await auth0Client.getIdTokenClaims()
              setToken(token?.__raw || "")
              navigate("/mypage")
              return
            } catch (callbackError: any) {
              console.error("Callback error:", callbackError)
              if (
                callbackError.error === "invalid_state" ||
                callbackError.message?.includes("Invalid state")
              ) {
                window.history.replaceState({}, document.title, window.location.pathname)
              }
            }
          }

          // Check authentication status
          const isAuthenticated = await auth0Client.isAuthenticated()
          if (isAuthenticated) {
            const user = await auth0Client.getUser()
            if (user) {
              const token = await auth0Client.getIdTokenClaims()
              setToken(token?.__raw || "")
            }
          } else {
            performLoginRedirect()
          }
        } catch (error) {
          console.error("Auth0 error:", error)
          performLoginRedirect()
        }
      }
      checkIsAuthenticated()
    }
  }, [auth0Client])

  useEffect(() => {
    if (!token) {
      return
    }
    // 将来的にサーバーサイドでの認証になった場合は、単にこのエンドポイントを叩けばよい
    UserService.getMyProfile(token)
      .then((res) => {
        setUser({
          id: res.id,
          username: res.firstName + " " + res.lastName,
          email: res.email!,
          type: res.userType!,
          profileURL: "",
          authID: res.cognitoId!,
          companyID: res.companyId!,
          token,
        })
        setDatadogUser(res.id)
      })
      .catch(() => {})
  }, [token])

  useEffect(() => {
    if (!liff.isInClient() && !token) {
      return
    }
    liff.init({ liffId: process.env.REACT_APP_LIFF_ID as string }).then(() => {
      const idToken = liff.getIDToken()
      if (idToken) {
        setLineIdToken(idToken)
      }
    })
  }, [token])

  const logout = () => {
    if (!auth0Client) {
      return
    }

    clearDatadogUser()
    auth0Client.logout()
  }

  return (
    <UserAuthContext.Provider
      value={{ session, setSession, auth0Client, user, logout, lineIdToken }}
    >
      {children}
    </UserAuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(UserAuthContext) as IUserAuthContext

  if (context === undefined) {
    throw new Error("useAuth must be used within a AuthProvider")
  }

  return context
}
